import { useEffect } from 'react'
import { createLogger } from '@/lib/logger'
import { isTauri } from '@/lib/utils/tauri'
import { useUIPreferencesStore } from '@/store/ui-preferences.store'

const logger = createLogger('hooks/useTauriLiquidGlass')

/**
 * Desktop window glass uses Tauri's native Liquid Glass effect
 * (`Effect.LiquidGlassRegular`: a real NSGlassEffectView behind the webview,
 * macOS 26+). Nothing here imitates glass in CSS: where the native effect
 * is unavailable (or turned off) the window stays flat.
 */
const FALLBACK_THEME_HEX = '#1a181b'
/** Strongest tint the opacity setting reaches: just short of solid. */
const MAX_TINT_ALPHA = 0.95
/** Thinnest tint any theme gets, so the window always carries some colour. */
const FLOOR_TINT_ALPHA = 0.25
/** Contrast cap for the readability floor (AAA for text). */
const MAX_GLASS_CONTRAST = 7
/** The floor keeps this share of the theme's own solid contrast. */
const GLASS_CONTRAST_SHARE = 0.85
/**
 * No theme's floor goes above this, so even mid-tone themes (whose solid
 * contrast is modest) keep visible glass at 0%; the slider adds the rest.
 */
const MAX_FLOOR_TINT_ALPHA = 0.85

type Rgb = [number, number, number]

const channelLinear = (c: number) => {
  const v = c / 255
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
}
const luminance = ([r, g, b]: Rgb) =>
  0.2126 * channelLinear(r) +
  0.7152 * channelLinear(g) +
  0.0722 * channelLinear(b)
const contrast = (a: number, b: number) =>
  (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)

/** `"276 9% 10%"` (a Tailwind HSL triple) → RGB. */
export function hslTripleToRgb(triple: string): Rgb | null {
  const m = /^\s*([\d.]+)\s+([\d.]+)%\s+([\d.]+)%/.exec(triple)
  if (!m) return null
  const h = Number(m[1]) / 360
  const s = Number(m[2]) / 100
  const l = Number(m[3]) / 100
  const f = (n: number) => {
    const k = (n + h * 12) % 12
    return l - s * Math.min(l, 1 - l) * Math.max(-1, Math.min(k - 3, 9 - k, 1))
  }
  return [f(0), f(8), f(4)].map(v => Math.round(v * 255)) as Rgb
}

/**
 * The thinnest tint that keeps a theme's icons and labels readable over the
 * glass, whatever is behind the window. The worst backdrop is the one on the
 * text's own side (white behind light text, black behind dark text); the
 * tint must restore 85% of the contrast the theme has when solid, capped at
 * 7:1, and never above 0.85 so glass always shows. Across the themes this
 * lands between ~0.64 (Egg White) and the 0.85 cap, with Cast Iron at ~0.72.
 */
export function minReadableTintAlpha(tint: Rgb, text: Rgb): number {
  const textL = luminance(text)
  const backdrop: Rgb = textL > 0.5 ? [255, 255, 255] : [0, 0, 0]
  const target = Math.min(
    MAX_GLASS_CONTRAST,
    GLASS_CONTRAST_SHARE * contrast(luminance(tint), textL),
  )
  for (let a = FLOOR_TINT_ALPHA; a < MAX_FLOOR_TINT_ALPHA; a += 0.01) {
    const mix = tint.map((c, i) => c * a + backdrop[i] * (1 - a)) as Rgb
    if (contrast(luminance(mix), textL) >= target) return a
  }
  return MAX_FLOOR_TINT_ALPHA
}

/**
 * The user's glass opacity (0–100, Appearance → Desktop) runs from the
 * theme's readability floor (0%) to just short of solid (100%).
 */
export function glassTintAlpha(
  opacity: number,
  minAlpha: number = FLOOR_TINT_ALPHA,
): number {
  const clamped = Math.min(100, Math.max(0, opacity)) / 100
  return minAlpha + (MAX_TINT_ALPHA - minAlpha) * clamped
}
const DARK_MODE_DARKEN_FACTOR = 0.28
const LIGHT_MODE_WHITE_BLEND_FACTOR = 0.34
const LIGHT_MODE_ON_DARK_SYSTEM_WHITE_BLEND_FACTOR = 0.72

function normalizeHexColor(value: string): string | null {
  const normalized = value.trim()
  if (!normalized) return null

  if (/^#[0-9a-fA-F]{6}$/.test(normalized)) {
    return normalized.toLowerCase()
  }

  if (/^#[0-9a-fA-F]{3}$/.test(normalized)) {
    const r = normalized[1]
    const g = normalized[2]
    const b = normalized[3]
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase()
  }

  return null
}

function withAlpha(hex: string, alpha: number): string {
  return `${hex}${alpha.toString(16).padStart(2, '0')}`
}

function darkenHexColor(hex: string, factor: number): string {
  const clampedFactor = Math.min(Math.max(factor, 0), 1)
  const r = Math.round(parseInt(hex.slice(1, 3), 16) * (1 - clampedFactor))
  const g = Math.round(parseInt(hex.slice(3, 5), 16) * (1 - clampedFactor))
  const b = Math.round(parseInt(hex.slice(5, 7), 16) * (1 - clampedFactor))
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b
    .toString(16)
    .padStart(2, '0')}`
}

function blendHexColors(
  baseHex: string,
  mixHex: string,
  ratio: number,
): string {
  const clampedRatio = Math.min(Math.max(ratio, 0), 1)
  const mix = (baseChannel: string, mixChannel: string) => {
    const base = parseInt(baseChannel, 16)
    const mixed = parseInt(mixChannel, 16)
    return Math.round(base + (mixed - base) * clampedRatio)
  }

  const r = mix(baseHex.slice(1, 3), mixHex.slice(1, 3))
  const g = mix(baseHex.slice(3, 5), mixHex.slice(3, 5))
  const b = mix(baseHex.slice(5, 7), mixHex.slice(5, 7))

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b
    .toString(16)
    .padStart(2, '0')}`
}

function hexToRgba(hex: string): [number, number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
    parseInt(hex.slice(7, 9), 16),
  ]
}

/**
 * Glass is shelved for now: the flat tinted ground is the one desktop look.
 * Flip this to offer it again; the support check below, the Appearance
 * toggle, the quick theme menu's switch and the hook all key off it, and a
 * preference someone saved while it was on stays stored but inert.
 */
export const TAURI_GLASS_AVAILABLE = false

/**
 * Whether the window can take native Liquid Glass (macOS 26+) and the
 * option is on offer. Shared by the glass hook and the appearance controls
 * that offer the toggle.
 */
export async function isNativeGlassSupported(): Promise<boolean> {
  if (!TAURI_GLASS_AVAILABLE || !isTauri()) return false
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    return Boolean(await invoke<boolean>('native_glass_supported'))
  } catch {
    return false
  }
}

export function useTauriLiquidGlass(): void {
  const tauriGlassOpacity = useUIPreferencesStore(
    state => state.tauriGlassOpacity,
  )
  const tauriGlassEnabled = useUIPreferencesStore(
    state => state.tauriGlassEnabled,
  )

  useEffect(() => {
    if (!isTauri()) return

    let disposed = false
    let observer: MutationObserver | null = null
    let colorSchemeMediaQuery: MediaQueryList | null = null

    const getTint = () => {
      const appIsDarkMode = document.documentElement.classList.contains('dark')
      const systemPrefersDark =
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
      const rootStyles = getComputedStyle(document.documentElement)
      const themeHex =
        normalizeHexColor(rootStyles.getPropertyValue('--sidebar-bg-hex')) ??
        FALLBACK_THEME_HEX

      // The tint moves away from the theme's text colour: darker under light
      // text, lighter under dark text, so more colour always means more
      // contrast (keying it to app dark mode could push it toward the text).
      const text =
        hslTripleToRgb(rootStyles.getPropertyValue('--sidebar-text')) ??
        ([250, 250, 250] as Rgb)
      const textIsLight = luminance(text) > 0.5
      const tintBase = textIsLight
        ? darkenHexColor(themeHex, DARK_MODE_DARKEN_FACTOR)
        : blendHexColors(
            themeHex,
            '#ffffff',
            appIsDarkMode || systemPrefersDark
              ? LIGHT_MODE_ON_DARK_SYSTEM_WHITE_BLEND_FACTOR
              : LIGHT_MODE_WHITE_BLEND_FACTOR,
          )
      const tintRgb = hexToRgba(withAlpha(tintBase, 255)).slice(0, 3) as Rgb
      const tintAlpha = Math.round(
        glassTintAlpha(tauriGlassOpacity, minReadableTintAlpha(tintRgb, text)) *
          255,
      )

      return hexToRgba(withAlpha(tintBase, tintAlpha))
    }

    const applyGlassMode = async () => {
      try {
        const { getCurrentWindow, Effect } =
          await import('@tauri-apps/api/window')
        if (disposed) return

        const window = getCurrentWindow()
        const supported = await isNativeGlassSupported()
        if (disposed) return

        document.documentElement.setAttribute(
          'data-liquid-glass-supported',
          supported ? 'true' : 'false',
        )

        // Glass is off until the user turns it on (Appearance → Desktop):
        // the flat tinted ground is the default look, and it matches the
        // web. The preference is never set on the user's behalf.
        if (!supported || !tauriGlassEnabled) {
          document.documentElement.setAttribute(
            'data-liquid-glass-active',
            'false',
          )
          // Flat: no effect at all (never a Visual Effect fallback, which
          // reads as milky rather than glass).
          await window.clearEffects()
          return
        }

        // Only the Liquid Glass effect is listed, so Tauri applies nothing on
        // macOS 15 and below instead of falling back to a material.
        await window.setEffects({
          // Regular is the frosted Liquid Glass Finder's sidebar uses; Clear
          // barely blurs, so text behind the window read through the chrome.
          effects: [Effect.LiquidGlassRegular],
          color: getTint(),
        })
        document.documentElement.setAttribute(
          'data-liquid-glass-active',
          'true',
        )
      } catch (error) {
        document.documentElement.setAttribute(
          'data-liquid-glass-active',
          'false',
        )
        logger.debug(
          '[useTauriLiquidGlass] Failed to configure liquid glass mode:',
          error,
        )
      }
    }

    void applyGlassMode()
    observer = new MutationObserver(() => {
      void applyGlassMode()
    })
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-sidebar-theme'],
    })
    colorSchemeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleColorSchemeChange = () => {
      void applyGlassMode()
    }
    colorSchemeMediaQuery.addEventListener('change', handleColorSchemeChange)

    return () => {
      disposed = true
      observer?.disconnect()
      colorSchemeMediaQuery?.removeEventListener(
        'change',
        handleColorSchemeChange,
      )
      document.documentElement.removeAttribute('data-liquid-glass-supported')
      document.documentElement.removeAttribute('data-liquid-glass-active')
    }
  }, [tauriGlassEnabled, tauriGlassOpacity])
}
